import React, { useEffect, useState } from 'react';
import api from '../api';
import { useToast } from '../context/ToastContext';
import { Key, Plus, Trash2, Webhook, CheckCircle, XCircle, Copy } from 'lucide-react';
import { formatDate } from '../utils';

export default function AdminIntegrations() {
  const toast = useToast();
  const [tokens, setTokens] = useState([]);
  const [logs, setLogs] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ name: '', company_id: '' });
  const [tab, setTab] = useState('tokens');
  const [revokeConfirm, setRevokeConfirm] = useState(null); // id of token pending revoke

  useEffect(() => { loadData(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const loadData = async () => {
    try {
      const [tokensRes, logsRes, companiesRes] = await Promise.all([
        api.get('/api/admin/api-tokens'),
        api.get('/api/admin/webhook-logs'),
        api.get('/api/admin/companies?limit=200')
      ]);
      setTokens(tokensRes.data.tokens);
      setLogs(logsRes.data.logs);
      setCompanies(companiesRes.data.companies);
    } catch (err) { console.error(err); toast.error('Kunne ikke laste integrasjonsdata'); }
  };

  const createToken = async () => {
    try {
      await api.post('/api/admin/api-tokens', form);
      setShowCreate(false);
      setForm({ name: '', company_id: '' });
      loadData();
    } catch (err) { console.error(err); toast.error('Kunne ikke opprette API-nøkkel'); }
  };

  const revokeToken = async (id) => {
    if (revokeConfirm !== id) { setRevokeConfirm(id); return; }
    try {
      await api.delete(`/api/admin/api-tokens/${id}`);
      toast.success('API-nøkkel deaktivert');
      loadData();
    } catch (err) {
      console.error(err); toast.error('Kunne ikke deaktivere nøkkel');
    } finally { setRevokeConfirm(null); }
  };

  return (
    <div data-testid="admin-integrations-page">
      <div className="mb-6">
        <h1 className="font-heading text-2xl font-bold text-white tracking-tight">Integrasjoner</h1>
        <p className="text-slate-500 text-sm mt-1">API-nøkler, webhook-logger og integrasjonsstatus</p>
      </div>

      <div className="flex gap-2 mb-6">
        {[['tokens', 'API-nøkler', Key], ['logs', 'Webhook-logger', Webhook]].map(([key, label, Icon]) => (
          <button key={key} onClick={() => setTab(key)} data-testid={`tab-${key}`}
            className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-colors ${tab === key ? 'bg-accent-blue text-white' : 'bg-navy-800 text-slate-400 border border-navy-600 hover:border-accent-blue'}`}>
            <Icon size={16} /> {label}
          </button>
        ))}
      </div>

      {tab === 'tokens' && (
        <div>
          <div className="flex justify-end mb-4">
            <button onClick={() => setShowCreate(true)} data-testid="create-api-token-button"
              className="flex items-center gap-2 px-4 py-2.5 bg-accent-blue hover:bg-accent-blue-hover text-white font-medium rounded-lg text-sm transition-colors">
              <Plus size={16} /> Ny API-nøkkel
            </button>
          </div>

          <div className="bg-navy-800 border border-navy-600 rounded-lg overflow-hidden">
            <table className="w-full">
              <thead>
                <tr className="border-b border-navy-600">
                  <th className="text-left px-4 py-3 text-xs font-semibold tracking-widest uppercase text-slate-500">Navn</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold tracking-widest uppercase text-slate-500">Nøkkel</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold tracking-widest uppercase text-slate-500">Omfang</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold tracking-widest uppercase text-slate-500">Status</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold tracking-widest uppercase text-slate-500">Opprettet</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold tracking-widest uppercase text-slate-500">Handlinger</th>
                </tr>
              </thead>
              <tbody>
                {tokens.length === 0 ? (
                  <tr><td colSpan="6" className="px-4 py-12 text-center text-slate-500">Ingen API-nøkler opprettet ennå</td></tr>
                ) : tokens.map(t => (
                  <tr key={t._id} className="border-b border-navy-600/50">
                    <td className="px-4 py-3.5 text-sm font-medium text-white">{t.name}</td>
                    <td className="px-4 py-3.5">
                      <div className="flex items-center gap-2">
                        <code className="text-xs text-slate-400 font-mono">{t.token?.substring(0, 20)}...</code>
                        <button onClick={() => navigator.clipboard.writeText(t.token)} className="text-slate-500 hover:text-accent-blue"><Copy size={12} /></button>
                      </div>
                    </td>
                    <td className="px-4 py-3.5">
                      <div className="flex gap-1">{(t.scopes || []).map(s => <span key={s} className="text-[10px] px-1.5 py-0.5 bg-navy-700 rounded text-slate-400">{s}</span>)}</div>
                    </td>
                    <td className="px-4 py-3.5">
                      <span className={`text-xs px-2 py-1 rounded ${t.active ? 'bg-accent-green/15 text-accent-green' : 'bg-accent-red/15 text-accent-red'}`}>
                        {t.active ? 'Aktiv' : 'Deaktivert'}
                      </span>
                    </td>
                    <td className="px-4 py-3.5 text-sm text-slate-400">{t.created_at ? formatDate(t.created_at) : '—'}</td>
                    <td className="px-4 py-3.5">
                      {t.active && (
                        revokeConfirm === t._id ? (
                          <div className="flex items-center gap-2">
                            <span className="text-xs text-accent-red">Sikker?</span>
                            <button onClick={() => revokeToken(t._id)} className="text-xs px-2 py-0.5 bg-accent-red/20 text-accent-red rounded hover:bg-accent-red/30">Ja</button>
                            <button onClick={() => setRevokeConfirm(null)} className="text-xs px-2 py-0.5 border border-navy-600 text-slate-400 rounded hover:bg-navy-700">Nei</button>
                          </div>
                        ) : (
                          <button onClick={() => revokeToken(t._id)} className="text-slate-500 hover:text-accent-red transition-colors" title="Deaktiver nøkkel"><Trash2 size={14} /></button>
                        )
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {showCreate && (
            <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 px-4">
              <div className="bg-navy-800 border border-navy-600 rounded-xl p-6 w-full max-w-md">
                <h2 className="font-heading text-lg font-semibold text-white mb-4">Ny API-nøkkel</h2>
                <div className="space-y-4">
                  <input type="text" placeholder="Navn på nøkkel" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                    className="w-full px-4 py-3 bg-navy-900 border border-navy-600 rounded-lg text-sm text-white focus:outline-none focus:ring-2 focus:ring-accent-blue" />
                  <select value={form.company_id} onChange={(e) => setForm({ ...form, company_id: e.target.value })}
                    className="w-full px-4 py-3 bg-navy-900 border border-navy-600 rounded-lg text-sm text-white focus:outline-none focus:ring-2 focus:ring-accent-blue">
                    <option value="">Global (alle bedrifter)</option>
                    {companies.map(c => <option key={c._id} value={c._id}>{c.name}</option>)}
                  </select>
                  <div className="flex gap-2">
                    <button onClick={createToken} className="flex-1 py-3 bg-accent-blue text-white font-semibold rounded-lg">Opprett</button>
                    <button onClick={() => setShowCreate(false)} className="flex-1 py-3 bg-navy-700 text-slate-300 rounded-lg">Avbryt</button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {tab === 'logs' && (
        <div className="bg-navy-800 border border-navy-600 rounded-lg overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="border-b border-navy-600">
                <th className="text-left px-4 py-3 text-xs font-semibold tracking-widest uppercase text-slate-500">Tidspunkt</th>
                <th className="text-left px-4 py-3 text-xs font-semibold tracking-widest uppercase text-slate-500">Hendelse</th>
                <th className="text-left px-4 py-3 text-xs font-semibold tracking-widest uppercase text-slate-500">URL</th>
                <th className="text-left px-4 py-3 text-xs font-semibold tracking-widest uppercase text-slate-500">Status</th>
                <th className="text-left px-4 py-3 text-xs font-semibold tracking-widest uppercase text-slate-500">Resultat</th>
              </tr>
            </thead>
            <tbody>
              {logs.length === 0 ? (
                <tr><td colSpan="5" className="px-4 py-12 text-center text-slate-500">Ingen webhook-logger ennå</td></tr>
              ) : logs.map(log => (
                <tr key={log._id} className="border-b border-navy-600/50">
                  <td className="px-4 py-3.5 text-sm text-slate-400">{log.created_at ? formatDate(log.created_at) : '—'}</td>
                  <td className="px-4 py-3.5 text-sm text-white">{log.event}</td>
                  <td className="px-4 py-3.5 text-xs text-slate-400 font-mono truncate max-w-[200px]">{log.url}</td>
                  <td className="px-4 py-3.5 text-sm font-mono text-slate-400">{log.status_code || '—'}</td>
                  <td className="px-4 py-3.5">
                    {log.success ? <CheckCircle size={16} className="text-accent-green" /> : <XCircle size={16} className="text-accent-red" />}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
